import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Pencil, Check, X, RotateCcw, Search } from "lucide-react";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageTitle } from "@/components/admin/AdminLayout";

const LANG_LABELS = { en: "Anglais", de: "Allemand", it: "Italien" };

function Progress({ code, s }) {
  const pct = s.needed ? Math.round((s.done / s.needed) * 100) : 100;
  return (
    <div className="card p-5" data-testid={`tr-status-${code}`}>
      <div className="flex items-center justify-between"><p className="font-medium text-forest">{LANG_LABELS[code]}</p><span className="text-xs text-charcoal/60">{pct} %</span></div>
      <div className="h-2 rounded-full bg-sand/60 mt-3 overflow-hidden"><div className="h-full bg-sage transition-all" style={{ width: `${pct}%` }} /></div>
      <p className="text-xs text-charcoal/60 mt-2">{s.done} / {s.needed} textes traduits{s.manual ? ` · ${s.manual} corrigé(s) à la main` : ""}{s.missing ? ` · ${s.missing} en attente (affichés en français)` : ""}</p>
    </div>
  );
}

function Row({ it, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(it.text);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(it.text); }, [it.text]);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/admin/translations/${it.id}`, { text: val }); onSaved(r.data); setEdit(false); toast.success("Correction enregistrée : elle ne sera plus modifiée par l'IA"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const regen = async () => {
    if (it.origin === "manual" && !window.confirm("Remplacer votre correction par une nouvelle traduction automatique ?")) return;
    setBusy(true);
    try { const r = await api.post(`/admin/translations/${it.id}/regenerate`); onSaved(r.data); toast.success("Traduction régénérée"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <tr className="align-top border-t border-sand/70" data-testid={`tr-row-${it.id}`}>
      <td className="px-3 py-3 text-sm text-charcoal/80 whitespace-pre-line w-[42%]">{it.source}{it.context && <span className="block text-[10px] uppercase tracking-wider text-charcoal/40 mt-1">{it.context}</span>}</td>
      <td className="px-3 py-3 text-sm w-[42%]">
        {edit ? (
          <textarea className="input min-h-[90px] text-sm" value={val} onChange={(e) => setVal(e.target.value)} data-testid={`tr-edit-input-${it.id}`} />
        ) : <p className="whitespace-pre-line text-forest">{it.text}</p>}
        <p className="text-[10px] text-charcoal/40 mt-1">{it.origin === "manual" ? "Correction manuelle" : "Traduction IA"} · {fmtDateTime(it.updated_at)}</p>
      </td>
      <td className="px-3 py-3 whitespace-nowrap text-right">
        {edit ? (
          <div className="inline-flex gap-1">
            <button onClick={save} disabled={busy} className="btn-primary px-3 py-2 min-h-0 text-xs" data-testid={`tr-save-${it.id}`}><Check size={14} /> Enregistrer</button>
            <button onClick={() => { setEdit(false); setVal(it.text); }} className="btn-ghost px-3 py-2 min-h-0 text-xs" aria-label="Annuler"><X size={14} /></button>
          </div>
        ) : (
          <div className="inline-flex gap-1">
            <button onClick={() => setEdit(true)} className="btn-ghost px-3 py-2 min-h-0 text-xs" data-testid={`tr-edit-${it.id}`}><Pencil size={13} /> Corriger</button>
            <button onClick={regen} disabled={busy} className="btn-ghost px-3 py-2 min-h-0 text-xs" title="Nouvelle traduction automatique" data-testid={`tr-regen-${it.id}`}><RotateCcw size={13} className={busy ? "animate-spin" : ""} /></button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function TranslationsAdmin() {
  const [status, setStatus] = useState(null);
  const [lang, setLang] = useState("en");
  const [q, setQ] = useState("");
  const [origin, setOrigin] = useState("");
  const [data, setData] = useState({ items: [], total: 0, missing: [] });
  const [loading, setLoading] = useState(false);

  const loadStatus = useCallback(() => api.get("/admin/translations/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/translations", { params: { lang, q, origin } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [lang, q, origin]);
  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
  useEffect(() => { // rafraîchit l'avancement pendant que l'IA traduit
    if (!status?.running) return undefined;
    const h = setInterval(() => { loadStatus(); load(); }, 8000);
    return () => clearInterval(h);
  }, [status?.running, loadStatus, load]);

  const warm = async () => {
    try { const r = await api.post("/admin/translations/warm"); setStatus(r.data); toast.success("Traduction des textes manquants lancée"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onSaved = (it) => { setData((d) => ({ ...d, items: d.items.map((x) => (x.id === it.id ? it : x)) })); loadStatus(); load(); };

  return (
    <div data-testid="translations-admin">
      <PageTitle title="Traductions" text="Le site est rédigé en français ; l'anglais, l'allemand et l'italien sont traduits automatiquement par l'IA puis mis en cache. Vos corrections manuelles ne sont jamais écrasées.">
        <div className="flex gap-2">
          <button onClick={() => { loadStatus(); load(); }} className="btn-ghost" data-testid="tr-refresh"><RefreshCw size={15} /> Actualiser</button>
          <button onClick={warm} className="btn-primary" disabled={status && !status.llm_configured} data-testid="tr-warm"><Sparkles size={15} /> Traduire les textes manquants</button>
        </div>
      </PageTitle>

      {status && !status.llm_configured && <div className="card p-4 mb-6 text-sm text-red-700 border-red-200 bg-red-50">Clé <code>EMERGENT_LLM_KEY</code> absente du fichier .env du serveur : la traduction automatique est désactivée (le site reste en français).</div>}
      {status?.last_error && <div className="card p-4 mb-6 text-xs text-amber-800 bg-amber-50 border-amber-200">Dernière erreur IA : {status.last_error}</div>}
      {status && (
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {Object.entries(status.langs).map(([code, s]) => <Progress key={code} code={code} s={s} />)}
        </div>
      )}
      {status?.running && <p className="text-xs text-sage mb-4 inline-flex items-center gap-2" data-testid="tr-running"><RefreshCw size={12} className="animate-spin" /> Traduction en cours ({status.pending} texte(s) en file d’attente)…</p>}

      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="inline-flex rounded-full border border-sand bg-white p-1" role="tablist">
          {Object.entries(LANG_LABELS).map(([code, label]) => (
            <button key={code} role="tab" aria-selected={lang === code} onClick={() => setLang(code)} data-testid={`tr-tab-${code}`} className={`px-4 py-1.5 rounded-full text-xs font-medium ${lang === code ? "bg-forest text-ecru" : "text-forest"}`}>{label}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" /><input className="input pl-9" placeholder="Rechercher un texte (français ou traduit)" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tr-search" /></div>
        <select className="input w-auto" value={origin} onChange={(e) => setOrigin(e.target.value)} data-testid="tr-origin-filter">
          <option value="">Toutes</option><option value="ai">Traductions IA</option><option value="manual">Corrections manuelles</option>
        </select>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-left min-w-[720px]">
          <thead className="bg-cream/70 text-[11px] uppercase tracking-wider text-charcoal/60"><tr><th className="px-3 py-2">Français (source)</th><th className="px-3 py-2">{LANG_LABELS[lang]}</th><th className="px-3 py-2" /></tr></thead>
          <tbody>
            {data.items.map((it) => <Row key={it.id} it={it} onSaved={onSaved} />)}
            {!loading && data.items.length === 0 && <tr><td colSpan={3} className="px-3 py-10 text-center text-sm text-charcoal/50">Aucune traduction pour ce filtre.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-charcoal/50 mt-2">{data.items.length} / {data.total} affichée(s).</p>

      {data.missing.length > 0 && (
        <div className="mt-8" data-testid="tr-missing">
          <h2 className="font-serif text-xl text-forest mb-2">En attente de traduction ({data.missing.length})</h2>
          <p className="text-xs text-charcoal/60 mb-3">Ces textes s’affichent en français dans la langue choisie jusqu’à leur traduction automatique (quelques instants après chaque modification).</p>
          <ul className="card divide-y divide-sand/70 text-sm">{data.missing.slice(0, 50).map((m, i) => <li key={i} className="px-4 py-2 text-charcoal/70 truncate">{m.source}</li>)}</ul>
        </div>
      )}
    </div>
  );
}
